import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Swal from "sweetalert2";
import {
  OrderResponse,
  getOrderClientName,
  getOrderClientUserId,
  useOrders,
} from "../modules/hooks/useOrders";
import {
  CardapioPedido,
  CARDAPIO_PEDIDO_STATUS,
  fetchCardapioPedidos,
  fetchClienteById,
} from "../modules/cardapio/utils/cardapio.api";
import {
  fetchEmployees,
  CaixaEmployee,
} from "../modules/caixa-financeiro/utils/caixa.api";
import {
  collectClienteWristbands,
  effectiveCommissionPercent,
  formatCurrency,
  formatPedidosWaiterNames,
  mergePedidoCartLines,
  parseCurrency,
  pedidoMatchesClient,
  printConferenceNote,
  sumPedidosCommission,
  sumPedidosTotal,
} from "../modules/cardapio/utils/cardapio.utils";
import { formatMoneyRs } from "../utils/money";
import { CardapioPedidoPaymentModal } from "../modules/cardapio/components/CardapioPedidoPaymentModal";
import "../modules/cardapio/styles/cardapio-cliente.css";

interface PrintProps {
  value?: string | number;
  order?: OrderResponse;
  clientName?: string;
  /** Usuário da sessão no dashboard (consumo em aberto, igual /cardapio/pedido). */
  clientUserId?: number;
  /** Códigos de pulseira já conhecidos na sessão (content/conteudo). */
  wristbandCodes?: string[];
}

function resolveClientName(
  order: OrderResponse | undefined,
  users: any[] | undefined,
  fallback?: string,
): string {
  if (fallback?.trim()) return fallback.trim();
  if (!order) return "—";
  const name = getOrderClientName(order, users);
  return name || "—";
}

function formatOrderIssuedAt(order?: OrderResponse): string {
  if (!order) return new Date().toLocaleString("pt-BR");
  const datePart = String(order.data || "").trim();
  const timePart = String(order.hora || "").trim();
  if (datePart && timePart) return `${datePart} ${timePart}`;
  if (order.data_hora_unix) {
    return new Date(order.data_hora_unix * 1000).toLocaleString("pt-BR");
  }
  return new Date().toLocaleString("pt-BR");
}

function isOrderPaid(order?: OrderResponse): boolean {
  return String(order?.status_pedido || "")
    .toLowerCase()
    .includes("pago");
}

function resolveClientUserId(
  order: OrderResponse | undefined,
  clientUserId?: number,
): number {
  const fromProp = Number(clientUserId || 0);
  if (fromProp > 0) return fromProp;
  if (order) return getOrderClientUserId(order);
  return 0;
}

const Print: React.FC<PrintProps> = ({
  value,
  order,
  clientName,
  clientUserId,
  wristbandCodes,
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [loadingOpen, setLoadingOpen] = useState(false);
  const [openPedidos, setOpenPedidos] = useState<CardapioPedido[]>([]);
  const [employees, setEmployees] = useState<CaixaEmployee[]>([]);
  const [wristband, setWristband] = useState("");
  const paidPedidoIdsRef = useRef<Set<number>>(new Set());
  const { userData, updateStatusMutation, refetch } = useOrders();

  const resolvedUserId = resolveClientUserId(order, clientUserId);
  const extraBandsKey = (wristbandCodes || []).filter(Boolean).join("|");
  const extraBands = useMemo(
    () => extraBandsKey.split("|").filter(Boolean),
    [extraBandsKey],
  );
  const resolvedName = useMemo(
    () => resolveClientName(order, userData, clientName),
    [order, userData, clientName],
  );

  const isPaid = isOrderPaid(order);
  /** Sessão no dashboard ou pedido em aberto: mesma lógica de /cardapio/pedido. */
  const useOpenConsumption = !isPaid;

  const lines = useMemo(() => {
    if (useOpenConsumption) {
      return openPedidos.length > 0 ? mergePedidoCartLines(openPedidos) : [];
    }
    return (order?.itens || []).map((item, idx) => ({
      _id_cardapio_item: Number(item.produto_id) || idx + 1,
      item_name: item.produto_nome,
      quantity: Number(item.quantidade) || 0,
      unit_price: Number(item.valor_unitario) || 0,
    }));
  }, [useOpenConsumption, openPedidos, order]);

  const total = useMemo(() => {
    if (useOpenConsumption) {
      return openPedidos.length > 0 ? sumPedidosTotal(openPedidos) : 0;
    }
    return Number(order?.valor_total_pedido ?? 0);
  }, [useOpenConsumption, openPedidos, order]);

  const commissionPercent = useMemo(
    () =>
      useOpenConsumption
        ? effectiveCommissionPercent(openPedidos[0]?.commission_percent)
        : 0,
    [useOpenConsumption, openPedidos],
  );

  const commissionValue = useMemo(
    () => (useOpenConsumption ? sumPedidosCommission(openPedidos) : 0),
    [useOpenConsumption, openPedidos],
  );

  const waiterName = useMemo(
    () =>
      useOpenConsumption ? formatPedidosWaiterNames(openPedidos) : "",
    [useOpenConsumption, openPedidos],
  );

  const issuedAt = useMemo(() => {
    if (useOpenConsumption && openPedidos.length) {
      const latest = openPedidos
        .map((p) => p.created_at)
        .filter(Boolean)
        .sort()
        .at(-1);
      if (latest) {
        try {
          return new Date(latest).toLocaleString("pt-BR");
        } catch {
          return String(latest);
        }
      }
    }
    return formatOrderIssuedAt(order);
  }, [useOpenConsumption, openPedidos, order]);

  const paymentEmployee = useMemo(() => {
    if (waiterName) {
      const names = waiterName.split(",").map((n) => n.trim());
      const found = employees.find((e) => names.includes(e.name));
      if (found) return found;
    }
    return employees[0] ?? null;
  }, [employees, waiterName]);

  const canPay =
    useOpenConsumption && lines.length > 0 && !!paymentEmployee && !!wristband;

  const displayValue = useMemo(() => {
    if (useOpenConsumption) {
      return formatCurrency(total);
    }
    if (typeof value === "number") return formatMoneyRs(value);
    if (value != null && String(value).trim()) {
      return formatMoneyRs(String(value).trim());
    }
    return formatMoneyRs(order?.valor_total_pedido ?? 0);
  }, [useOpenConsumption, total, value, order]);

  const clickableAmount = useMemo(() => {
    if (useOpenConsumption) return Number(total) || 0;
    if (typeof value === "number") return Number.isFinite(value) ? value : 0;
    if (value != null && String(value).trim()) {
      return parseCurrency(String(value).trim());
    }
    return parseCurrency(order?.valor_total_pedido ?? 0);
  }, [useOpenConsumption, total, value, order]);

  const canOpenModal =
    !loadingOpen && Math.round(clickableAmount * 100) > 0;

  const loadOpenConsumption = useCallback(
    async (cancelled: { current: boolean }, opts?: { silent?: boolean }) => {
      if (!useOpenConsumption || resolvedUserId <= 0) {
        setOpenPedidos([]);
        setWristband("");
        return;
      }

      if (!opts?.silent) setLoadingOpen(true);
      try {
        const [emps, cliente] = await Promise.all([
          fetchEmployees().catch(() => [] as CaixaEmployee[]),
          fetchClienteById(resolvedUserId).catch(() => null),
        ]);
        if (cancelled.current) return;
        setEmployees(emps);

        const bands = collectClienteWristbands(cliente, [
          ...extraBands,
          resolvedUserId,
        ]);
        setWristband(bands[0] || extraBands[0] || "");

        const pedidos = await fetchCardapioPedidos(
          undefined,
          CARDAPIO_PEDIDO_STATUS.EM_ABERTO,
        ).catch(() => [] as CardapioPedido[]);
        if (cancelled.current) return;

        const filtered = pedidos.filter(
          (p) =>
            pedidoMatchesClient(p, resolvedUserId, bands) &&
            !paidPedidoIdsRef.current.has(Number(p._id)),
        );
        setOpenPedidos(filtered);
      } finally {
        if (!cancelled.current && !opts?.silent) setLoadingOpen(false);
      }
    },
    [useOpenConsumption, resolvedUserId, extraBands],
  );

  /** Pré-carrega total em aberto (lista do dashboard). */
  useEffect(() => {
    if (!useOpenConsumption || resolvedUserId <= 0) {
      setOpenPedidos([]);
      return;
    }
    const cancelled = { current: false };
    loadOpenConsumption(cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [useOpenConsumption, resolvedUserId, loadOpenConsumption]);

  /** Recarrega ao abrir o modal. */
  useEffect(() => {
    if (!modalIsOpen || !useOpenConsumption) return;
    const cancelled = { current: false };
    loadOpenConsumption(cancelled);
    return () => {
      cancelled.current = true;
    };
  }, [modalIsOpen, useOpenConsumption, loadOpenConsumption]);

  /** Pagamento no dashboard, cardápio ou topbar zera o valor em aberto. */
  useEffect(() => {
    if (!useOpenConsumption || resolvedUserId <= 0) return;
    const onChanged = () => {
      void loadOpenConsumption({ current: false }, { silent: true });
    };
    window.addEventListener("cardapio:pedido-changed", onChanged);
    return () => window.removeEventListener("cardapio:pedido-changed", onChanged);
  }, [useOpenConsumption, resolvedUserId, loadOpenConsumption]);

  const openModal = () => {
    if (!canOpenModal) return;
    setModalIsOpen(true);
  };
  const closeModal = () => {
    setModalIsOpen(false);
    setPaymentOpen(false);
  };

  const handlePrint = () => {
    if (!lines.length) {
      Swal.fire(
        "Atenção",
        "Não há itens para imprimir na nota de conferência.",
        "warning",
      );
      return;
    }
    const ok = printConferenceNote({
      clientName: resolvedName,
      issuedAt,
      lines: lines.map((l) => ({
        item_name: l.item_name,
        quantity: l.quantity,
        unit_price: l.unit_price,
      })),
      total,
      commissionPercent,
      commissionValue,
    });
    if (!ok) {
      Swal.fire(
        "Atenção",
        "Não foi possível abrir a janela de impressão. Verifique o bloqueio de pop-ups.",
        "warning",
      );
    }
  };

  const handlePay = () => {
    if (!canPay) {
      Swal.fire(
        "Atenção",
        isPaid
          ? "Este pedido já está pago."
          : !wristband
            ? "Cliente sem pulseira vinculada para pagamento."
            : !paymentEmployee
              ? "Garçom do pedido não encontrado entre os funcionários cadastrados."
              : "Não há consumo em aberto para pagar.",
        "warning",
      );
      return;
    }
    setPaymentOpen(true);
  };

  const handlePaymentSuccess = async () => {
    openPedidos.forEach((p) => paidPedidoIdsRef.current.add(Number(p._id)));
    setOpenPedidos([]);
    setPaymentOpen(false);
    if (order) {
      try {
        await updateStatusMutation.mutateAsync({
          order,
          status: "Pago",
        });
      } catch {
        // pagamento no caixa já registrado; status pode falhar em legado
      }
    }
    await refetch();
    window.dispatchEvent(new Event("cardapio:pedido-changed"));
    window.dispatchEvent(new Event("caixa:session-changed"));
    closeModal();
    await loadOpenConsumption({ current: false }, { silent: true });
    Swal.fire("Sucesso", "Pagamento registrado com sucesso!", "success");
  };

  return (
    <div>
      <span
        className="fw-bold"
        style={{
          cursor: canOpenModal ? "pointer" : "default",
          transition: "0.2s",
          color: isPaid ? "green" : "#f6c000",
        }}
        onClick={canOpenModal ? openModal : undefined}
        title={canOpenModal ? "Abrir Nota de Conferência" : undefined}
        role={canOpenModal ? "button" : undefined}
      >
        {loadingOpen && useOpenConsumption && !modalIsOpen
          ? "…"
          : displayValue}
      </span>

      {!paymentOpen && (
        <Modal show={modalIsOpen} onHide={closeModal} centered size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Nota de Conferência</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
              <div>
                <div className="fw-semibold text-gray-800">{resolvedName}</div>
                <div className="text-muted fs-7">
                  {loadingOpen
                    ? "Carregando consumo..."
                    : useOpenConsumption
                      ? openPedidos.length
                        ? `${openPedidos.length} pedido(s) em aberto · Garçom: ${waiterName || "—"}`
                        : "Nenhum consumo em aberto para este cliente."
                      : "Pedido histórico"}
                </div>
              </div>
              <div className="fs-3 fw-bold text-primary">
                {formatCurrency(total)}
              </div>
            </div>

            <div className="conf-note border rounded">
              <h2>Nota de Conferência</h2>
              <div className="conf-meta">
                <div>Cliente: {resolvedName}</div>
                <div>Emissão: {issuedAt}</div>
                <div>
                  Status:{" "}
                  {isPaid ? (
                    <span className="text-success fw-bold">Pago</span>
                  ) : (
                    <span className="text-warning fw-bold">Em aberto</span>
                  )}
                </div>
              </div>
              <div className="conf-divider" />
              {loadingOpen && useOpenConsumption ? (
                <div className="text-center text-muted py-4">
                  Carregando consumo em aberto...
                </div>
              ) : (
                <>
                  <table>
                    <thead>
                      <tr>
                        <th>Item</th>
                        <th className="num">Qtd</th>
                        <th className="num">Unit.</th>
                        <th className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lines.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="text-center text-muted py-4"
                          >
                            {isPaid
                              ? "Sem itens nesta venda."
                              : "Sem consumo em aberto para este cliente."}
                          </td>
                        </tr>
                      ) : (
                        lines.map((line, idx) => (
                          <tr key={`${line.item_name}-${idx}`}>
                            <td>{line.item_name}</td>
                            <td className="num">{line.quantity}</td>
                            <td className="num">
                              {formatCurrency(line.unit_price)}
                            </td>
                            <td className="num">
                              {formatCurrency(line.unit_price * line.quantity)}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                  <div className="conf-divider" />
                  <div className="conf-total">
                    <span>TOTAL</span>
                    <span>{formatCurrency(total)}</span>
                  </div>
                  {Number(commissionValue) > 0 && (
                    <div className="conf-meta mt-2">
                      <div>
                        Comissão ({Number(commissionPercent) || 0}%):{" "}
                        {formatCurrency(commissionValue)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-end">
            <Button variant="light" size="sm" onClick={closeModal}>
              Fechar
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="btn-light-primary"
              onClick={handlePrint}
              disabled={lines.length === 0 || loadingOpen}
            >
              Imprimir Nota
            </Button>
            {useOpenConsumption && (
              <Button
                variant="success"
                size="sm"
                onClick={handlePay}
                disabled={!canPay || loadingOpen}
              >
                Pagar
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      )}

      {paymentEmployee && (
        <CardapioPedidoPaymentModal
          show={paymentOpen}
          onHide={() => setPaymentOpen(false)}
          amount={total}
          waiterId={paymentEmployee._id}
          waiterName={paymentEmployee.name}
          wristband={wristband || resolvedName}
          commissionPercent={commissionPercent}
          cart={lines}
          pedidoIds={openPedidos.map((p) => p._id)}
          _id_usuario={
            openPedidos.find((p) => Number(p._id_usuario) > 0)?._id_usuario ||
            resolvedUserId ||
            undefined
          }
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}
    </div>
  );
};

export default Print;
